"use client";

import * as React from "react";
import { Badge } from "@/components/ui/primitives/Badge";
import { Button } from "@/components/ui/primitives/Button";
import { IconButton } from "@/components/ui/primitives/IconButton";
import { Input } from "@/components/ui/primitives/Input";
import { SegmentedButton } from "@/components/ui/primitives/SegmentedButton";
import { Textarea } from "@/components/ui/primitives/Textarea";
import useAutoFocus from "@/lib/useAutoFocus";
import { capitalize, cn } from "@/lib/utils";
import { Pencil, Trash2, Pin, PinOff } from "lucide-react";
import { useReminders, Reminder, Group, Source, Domain } from "./useReminders";
import styles from "./ReminderList.module.css";

export function ReminderList() {
  const { filtered, updateReminder, removeReminder } = useReminders();
  const handleChange = React.useCallback(
    (id: string, partial: Partial<Reminder>) => {
      updateReminder(id, partial);
    },
    [updateReminder],
  );
  const handleDelete = React.useCallback(
    (id: string) => {
      removeReminder(id);
    },
    [removeReminder],
  );

  return (
    <>
      <div className="grid grid-cols-12 gap-[var(--space-3)]">
        {filtered.map((reminder) => (
          <div key={reminder.id} className="col-span-12 md:col-span-6 lg:col-span-4">
            <RemTile
              reminder={reminder}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          </div>
        ))}
      </div>

      {filtered.length === 0 && <EmptyState />}
    </>
  );
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-card border border-card-hairline-60 bg-surface card-pad text-ui font-medium text-muted-foreground">
      <p>Nothing here. Add one clear sentence you’ll read in champ select.</p>
    </div>
  );
}

type RemTileProps = {
  reminder: Reminder;
  onChange: (id: string, partial: Partial<Reminder>) => void;
  onDelete: (id: string) => void;
};

function RemTileBase({ reminder, onChange, onDelete }: RemTileProps) {
  const [editing, setEditing] = React.useState(false);
  const [title, setTitle] = React.useState(reminder.title);
  const [body, setBody] = React.useState(reminder.body ?? "");
  const [tagsText, setTagsText] = React.useState(reminder.tags.join(", "));
  const titleRef = React.useRef<HTMLInputElement | null>(null);
  const noteFieldId = `reminder-${reminder.id}-note`;
  const tagsFieldId = `reminder-${reminder.id}-tags`;

  useAutoFocus({ ref: titleRef, when: editing });

  React.useEffect(() => {
    if (editing) {
      return;
    }

    setTitle(reminder.title);
    setBody(reminder.body ?? "");
    setTagsText(reminder.tags.join(", "));
  }, [editing, reminder.body, reminder.tags, reminder.title]);

  const commitChange = React.useCallback(
    (partial: Partial<Reminder>) => {
      onChange(reminder.id, partial);
    },
    [onChange, reminder.id],
  );

  const handleDelete = React.useCallback(() => {
    onDelete(reminder.id);
  }, [onDelete, reminder.id]);

  const save = React.useCallback(() => {
    const cleanTags = tagsText
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    commitChange({
      title: title.trim() || "Untitled",
      body: body.trim(),
      tags: cleanTags,
    });
    setEditing(false);
  }, [body, commitChange, tagsText, title]);

  const pinned = !!reminder.pinned;
  const togglePinned = React.useCallback(() => {
    commitChange({ pinned: !pinned });
  }, [commitChange, pinned]);
  const togglePinnedLabel = React.useMemo(() => {
    const baseLabel = pinned ? "Unpin reminder" : "Pin reminder";
    return reminder.title ? `${baseLabel} ${reminder.title}` : baseLabel;
  }, [pinned, reminder.title]);
  const deleteLabel = React.useMemo(() => {
    const name = reminder.title.trim() || "Untitled reminder";
    return `Delete ${name}`;
  }, [reminder.title]);

  return (
    <article
      className={cn(
        "group relative rounded-card r-card-lg border border-card-hairline-60 bg-surface card-pad transition-colors focus-within:bg-surface-2",
        styles.card,
      )}
    >
      <div className="flex items-center justify-between gap-[var(--space-2)]">
        <div className="flex-1 min-w-0">
          {editing ? (
            <Input
              ref={titleRef}
              value={title}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(event.currentTarget.value)
              }
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
                event.key === "Enter" && save()
              }
              aria-label="Title"
              className="font-semibold uppercase tracking-wide"
            />
          ) : (
            <div className="flex items-center gap-[var(--space-2)] min-w-0">
              <h4
                className="font-semibold uppercase tracking-wide pr-[var(--space-2)] title-glow glitch leading-6 truncate"
                title={reminder.title}
              >
                {reminder.title}
              </h4>
              <Button
                size="sm"
                variant="quiet"
                onClick={() => {
                  setEditing(true);
                }}
                aria-label={
                  reminder.title ? `Edit reminder ${reminder.title}` : "Edit reminder"
                }
                className="shrink-0"
              >
                <Pencil aria-hidden="true" />
                Edit
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-[var(--space-1)]">
          <IconButton
            title={deleteLabel}
            aria-label={deleteLabel}
            onClick={handleDelete}
            size="sm"
            iconSize="sm"
            variant="quiet"
            className="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity"
          >
            <Trash2 />
          </IconButton>

          <IconButton
            aria-pressed={pinned}
            title={togglePinnedLabel}
            aria-label={togglePinnedLabel}
            onClick={togglePinned}
            size="sm"
            iconSize="sm"
          >
            {pinned ? <PinOff /> : <Pin />}
          </IconButton>
        </div>
      </div>

      <div className="mt-[var(--space-2)] space-y-[var(--space-3)]">
        {editing ? (
          <>
            <label
              className="text-label font-medium tracking-[0.02em] opacity-70"
              htmlFor={noteFieldId}
            >
              Note
            </label>
            <Textarea
              id={noteFieldId}
              aria-label="Body"
              placeholder="Short, skimmable sentence."
              value={body}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setBody(event.currentTarget.value)
              }
              className="rounded-[var(--radius-2xl)]"
              resize="resize-y"
              textareaClassName="min-h-[calc(var(--space-8)*2_+_var(--space-3))] leading-relaxed"
            />
            <label
              className="text-label font-medium tracking-[0.02em] opacity-70"
              htmlFor={tagsFieldId}
            >
              Tags
            </label>
            <Input
              id={tagsFieldId}
              aria-label="Tags (comma separated)"
              placeholder="tags, comma, separated"
              value={tagsText}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setTagsText(event.currentTarget.value)
              }
            />

            <div className="segmented flex flex-wrap -m-[var(--space-1)]">
              {(
                [
                  "pregame",
                  "laning",
                  "trading",
                  "tempo",
                  "review",
                  "quick",
                ] as Group[]
              ).map((groupKey) => (
                <SegmentedButton
                  key={groupKey}
                  onClick={() => commitChange({ group: groupKey })}
                  selected={reminder.group === groupKey}
                  className="m-[var(--space-1)]"
                >
                  {groupKey === "pregame" ? "Pre-Game" : capitalize(groupKey)}
                </SegmentedButton>
              ))}
            </div>

            <div className="segmented flex flex-wrap -m-[var(--space-1)]">
              {(["MLA", "BLA", "BrokenByConcept", "Custom"] as Source[]).map(
                (sourceKey) => (
                  <SegmentedButton
                    key={sourceKey}
                    onClick={() => commitChange({ source: sourceKey })}
                    selected={reminder.source === sourceKey}
                    className="m-[var(--space-1)]"
                  >
                    {sourceKey}
                  </SegmentedButton>
                ),
              )}
            </div>

            <div className="segmented flex flex-wrap -m-[var(--space-1)]">
              {(["Life", "League", "Learn"] as Domain[]).map((domainKey) => (
                <SegmentedButton
                  key={domainKey}
                  onClick={() => commitChange({ domain: domainKey })}
                  selected={(reminder.domain ?? "League") === domainKey}
                  className="m-[var(--space-1)]"
                >
                  {domainKey}
                </SegmentedButton>
              ))}
            </div>

            <div className="flex flex-col gap-[var(--space-2)] sm:flex-row sm:items-center sm:gap-[var(--space-2)]">
              <Button size="sm" onClick={save} className="w-full sm:w-auto">
                Save
              </Button>
              <Button
                size="sm"
                variant="quiet"
                onClick={() => {
                  setEditing(false);
                  setTitle(reminder.title);
                  setBody(reminder.body ?? "");
                  setTagsText(reminder.tags.join(", "));
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-ui font-medium">
              <span className="opacity-70">Note:</span>{" "}
              {reminder.body || (
                <span className="opacity-60">
                  No text. Click title to edit.
                </span>
              )}
            </p>

            <div className="mt-[var(--space-1)] flex items-center justify-between text-ui font-medium">
              <div className="flex items-center gap-[var(--space-2)]">
                <span
                  aria-hidden="true"
                  className="inline-block size-[var(--space-2)] rounded-full bg-accent-overlay"
                />
                <span className="text-label font-medium tracking-[0.02em]">{fmtDate(reminder.createdAt)}</span>
              </div>

              <Badge
                as="button"
                interactive
                tone="primary"
                size="sm"
                onClick={togglePinned}
                title={togglePinnedLabel}
                aria-label={togglePinnedLabel}
                aria-pressed={pinned}
                selected={pinned}
              >
                {pinned ? "Pinned" : "Pin"}
              </Badge>
            </div>
          </>
        )}
      </div>
    </article>
  );
}

const RemTile = React.memo(RemTileBase);
RemTile.displayName = "RemTile";

function pad(value: number) {
  return value < 10 ? `0${value}` : `${value}`;
}

function fmtDate(timestamp: number) {
  const date = new Date(timestamp);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

